/**
 * Parameter Schema Definition
 * 
 * This file defines the standardized parameter naming conventions and validation rules
 * for all API endpoints. It provides constants and schema definitions to ensure
 * consistency across the application.
 */

/**
 * Resource Types
 * 
 * These constants define the different resource types in the application.
 * Use these constants when referring to resource types to ensure consistency.
 */
export const RESOURCE_TYPES = {
  USER: 'user',
  ACTOR: 'actor',
  PORTFOLIO: 'portfolio',
  CHARACTER: 'character',
  VARIATION: 'variation',
  BOOK: 'book',
  LITERARY_GROUP: 'literaryGroup',
  SIGNED_URL: 'signedUrl'  // New resource type for signed URLs
};

/**
 * Resource ID Parameters
 * 
 * These constants define the parameter names for resource IDs.
 * Direct access for performance optimization.
 */
export const RESOURCE_ID_PARAMS = {
  [RESOURCE_TYPES.USER]: 'userId',
  [RESOURCE_TYPES.ACTOR]: 'actorId',
  [RESOURCE_TYPES.PORTFOLIO]: 'portfolioId',
  [RESOURCE_TYPES.CHARACTER]: 'characterId',
  [RESOURCE_TYPES.VARIATION]: 'variationId',
  [RESOURCE_TYPES.BOOK]: 'bookId',
  [RESOURCE_TYPES.LITERARY_GROUP]: 'literaryGroupId'
};

/**
 * Parameter to Resource Type Map
 * 
 * This map allows for reverse lookups from parameter name to resource type.
 * Used for validation and parameter processing.
 */
export const PARAM_TO_RESOURCE_TYPE = Object.entries(RESOURCE_ID_PARAMS).reduce(
  (acc, [resourceType, paramName]) => {
    acc[paramName] = resourceType;
    return acc;
  },
  {}
);

/**
 * Update PARAM_TO_RESOURCE_TYPE with aliases
 * 
 * This function will be called after importing RESOURCE_ID_PARAM_ALIASES
 * to add the aliases to the PARAM_TO_RESOURCE_TYPE map.
 */
export const updateParamToResourceTypeWithAliases = (aliases) => {
  Object.entries(aliases).forEach(([resourceType, aliasList]) => {
    aliasList.forEach(alias => {
      PARAM_TO_RESOURCE_TYPE[alias] = resourceType;
    });
  });
};

/**
 * Parameter Schema
 * 
 * This schema defines the validation rules for parameters.
 * It includes type information, format requirements, and default values.
 */
export const PARAMETER_SCHEMA = {
  // Resource identifiers
  [RESOURCE_TYPES.USER]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.USER],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.ACTOR]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.ACTOR],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.PORTFOLIO]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.PORTFOLIO],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.CHARACTER]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.CHARACTER],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.VARIATION]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.VARIATION],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.BOOK]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.BOOK],
    type: 'string',
    format: 'uuid',
    required: true
  },
  [RESOURCE_TYPES.LITERARY_GROUP]: {
    idParam: RESOURCE_ID_PARAMS[RESOURCE_TYPES.LITERARY_GROUP],
    type: 'string',
    format: 'uuid',
    required: true
  },

  // Query parameters
  pagination: {
    page: { type: 'number', default: 1 },
    limit: { type: 'number', default: 10 }
  },
  sorting: {
    sortBy: { type: 'string' },
    sortOrder: { type: 'string', enum: ['asc', 'desc'], default: 'asc' }
  },
  filtering: {
    status: { type: 'string' },
    priority: { type: 'string' },
    search: { type: 'string' }
  },
  inclusion: {
    include: { type: 'string' }
  }
};

/**
 * HTTP Methods
 * 
 * Constants for HTTP methods to ensure consistency.
 */
export const HTTP_METHODS = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
  PATCH: 'patch'
};

/**
 * Operation Types
 * 
 * Constants for common operation types to help with thunk creation.
 */
export const OPERATION_TYPES = {
  FETCH: 'fetch',
  FETCH_ALL: 'fetchAll',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete'
};

/**
 * Method to Operation Type Map
 * 
 * Maps HTTP methods to their typical operation types.
 */
export const METHOD_TO_OPERATION = {
  [HTTP_METHODS.GET]: OPERATION_TYPES.FETCH,
  [HTTP_METHODS.POST]: OPERATION_TYPES.CREATE,
  [HTTP_METHODS.PUT]: OPERATION_TYPES.UPDATE,
  [HTTP_METHODS.PATCH]: OPERATION_TYPES.UPDATE,
  [HTTP_METHODS.DELETE]: OPERATION_TYPES.DELETE
};

/**
 * Default Error Messages
 * 
 * Standard error messages for common validation failures.
 */
export const DEFAULT_ERROR_MESSAGES = {
  MISSING_ID: (resourceType) => `Valid ${resourceType} ID is required`,
  INVALID_UUID: (paramName) => `${paramName} must be a valid UUID`,
  SERVER_ERROR: 'An unexpected server error occurred',
  NETWORK_ERROR: 'Network error: Unable to connect to the server',
  UNAUTHORIZED: 'Authentication required: Please log in',
  FORBIDDEN: 'Access denied: You do not have permission to perform this action'
};

/**
 * Helper function to capitalize the first letter of a string
 * Used for generating type prefixes and other identifiers
 */
export const capitalize = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Generate a type prefix for Redux actions
 * 
 * @param {string} resourceType - The type of resource (e.g., 'character')
 * @param {string} operationType - The type of operation (e.g., 'fetch', 'update')
 * @returns {string} A formatted type prefix (e.g., 'character/fetchCharacter')
 */
export const generateTypePrefix = (resourceType, operationType) => {
  return `${resourceType}/${operationType}${capitalize(resourceType)}`;
};
